import React, { useState, useEffect, useRef } from "react";
import { useUsersContext } from "../context/usersContext";
import Icon from "./Icon";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import env from "../config";
import MarkdownContent from "./MarkdownContent";
import Uploadfile from "./uploadFiles";
import "./styles/v3.css";
import "./styles/main.css";
import "./styles/newUI.css";

const Messages = () => {
  const {
    SSO,
    addNewMessage,
    users,
    chats,
    streamResponse,
    isStreaming,
    showToast,
    ChatSubmit,
    userTyping,
    hideloader,
    agencyInfo,
    agencyLoading,
    threadId,
    getConversationId,
    setConversation,
    conversation,
  } = useUsersContext();

  const [micStatus, setMicStatus] = useState("unknown");
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const [autoChatLaunched, setAutoChatLaunched] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const getUrlParameter = (name) => {
    // 1. Try query string first
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get(name);
    if (searchParam) return searchParam;

    // 2. Try pathname: /agency_id=superYxadminagency_id or /something/agency_id=superYxadminagency_id
    const pathname = window.location.pathname;
    // Match /agency_id=superYxadminagency_id or /.../agency_id=superYxadminagency_id
    const pathMatch = pathname.match(new RegExp(`${name}=([^&/]+)`));
    return pathMatch ? pathMatch[1] : null;
  };

  const SuperAdminAgency = getUrlParameter("agency_id");

  // Speech Recognition hooks
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const processedLen = useRef(0);
  const micStreamRef = useRef(null);

  const formatTime = (timeString) => {
    if (!timeString) return "";

    try {
      if (timeString.includes("PM") || timeString.includes("AM")) {
        const [time, period] = timeString.split(" ");
        const [hours, minutes] = time.split(":");
        return `${hours}:${minutes} ${period}`;
      }

      // If it's a timestamp, convert it
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }

      return timeString;
    } catch (error) {
      return timeString;
    }
  };

  const [newMessage, setNewMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const messagesEndRef = useRef(null);
  const [fileType, setFileType] = useState("");

  const onFileSelect = (files) => {
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, userTyping, streamResponse]);

  useEffect(() => {
    let allMessages = [];

    // Get existing messages from conversation (old messages) - only if threadId exists
    if (threadId && Array.isArray(conversation)) {
      allMessages = conversation
        .map((item) => {
          if (item.message) {
            // Structure: { _id, message: {content, sender, ...}, createdAt }
            return {
              ...item.message,
              _id: item._id,
              createdAt: item.createdAt,
            };
          } else if (item.content) {
            // Direct message structure: { content, sender, time, ... }
            return item;
          }
          return null;
        })
        .filter(Boolean);

    }

    // Get new messages from current chat and merge
    if (currentChatId) {
      allMessages = conversation;
    }

    // Sort by date and set messages
    allMessages.sort((a, b) => {
      const timeA = new Date(a.createdAt || a.time || 0);
      const timeB = new Date(b.createdAt || b.time || 0);
      return timeA - timeB;
    });

    setMessages(allMessages);
    if (isTyping) setIsTyping(false);
  }, [users, currentChatId, conversation, threadId]);

  // Auto-start chat when component loads (for agent builder)
  useEffect(() => {
    if (
      !threadId &&
      !currentChatId &&
      !hasStartedChat &&
      !autoChatLaunched &&
      agencyInfo?.agentBuilderAccount?.id &&
      agencyInfo?.agentBuilderAssistantId &&
      SSO?.chat_username
    ) {
      setAutoChatLaunched(true);
      createNewChat();
    }
  }, [agencyInfo, SSO, currentChatId, hasStartedChat, autoChatLaunched]);

  // Handle existing conversation when threadId is present
  useEffect(() => {
    if (
      threadId &&
      !currentChatId &&
      !autoChatLaunched &&
      agencyInfo?.agentBuilderAccount?.id &&
      SSO?.chat_username
    ) {
      setAutoChatLaunched(true);
      setCurrentChatId(threadId);
      setHasStartedChat(true);

      // Trigger conversation loading using the context function
      getConversationId();

      // Send "edit mode" message for existing conversation with threadId
      const sendEditMode = async () => {
        const assistant = {
          modal_name: "openai/gpt-4.1",
          assistant_id: agencyInfo?.agentBuilderAssistantId,
          assistant_name: "agent builder",
        };

        await addNewMessage(
          threadId,
          "edit mode",
          "edit mode",
          "savetodb",
          [], // selectedFiles
          assistant,
          "notimage",
        );
      };

      sendEditMode();
    }
  }, [
    threadId,
    currentChatId,
    autoChatLaunched,
    agencyInfo,
    SSO,
    getConversationId,
    addNewMessage,
  ]);

  // Monitor userTyping from context to show/hide typing indicator
  useEffect(() => {
    if (userTyping && currentChatId) {
      setIsTyping(true);
    } else if (!userTyping) {
      setIsTyping(false);
    }
  }, [userTyping, currentChatId]);

  // Monitor hideloader to stop typing animation
  useEffect(() => {
    if (hideloader === false) {
      setIsTyping(false);
    }
  }, [hideloader]);


  useEffect(() => {
    if (!listening) return;
    if (transcript.length > processedLen.current) {
      const diff = transcript.slice(processedLen.current);
      setNewMessage((prev) => prev + diff);
      processedLen.current = transcript.length;
    }
  }, [transcript, listening, setNewMessage]);


  const createNewChat = async () => {
    try {
      const payload = {
        locationId:
          SuperAdminAgency === "superYxadminagency_id"
            ? "9DITYaucH469so88pN8a"
            : agencyInfo?.agentBuilderAccount?.id,
        username: SSO?.chat_username,
        assistant_id:
          SuperAdminAgency === "superYxadminagency_id"
            ? "asst_FPOU0HcPwzIwKDC2UrmH1IAz"
            : agencyInfo?.agentBuilderAssistantId,
        assistant_name: "agent builder",
      };

      const newChat = await ChatSubmit(payload);

      if (newChat?.id) {
        setCurrentChatId(newChat.id);
        if (!hasStartedChat && !threadId) {
          const assistant = {
            modal_name: "openai/gpt-4.1",
            assistant_id:
              SuperAdminAgency === "superYxadminagency_id"
                ? "asst_FPOU0HcPwzIwKDC2UrmH1IAz"
                : agencyInfo?.agentBuilderAssistantId,
            assistant_name: "agent builder",
          };

          await addNewMessage(
            newChat.id,
            "force restart",
            "force restart",
            "savetodb",
            [],
            assistant,
            "notimage",
          );

          setHasStartedChat(true);
        }

        return newChat.id;
      } else {
        showToast("Failed to create new chat!");
        return null;
      }
    } catch (error) {
      console.error("Error creating new chat:", error);
      showToast("Error creating new chat!");
      return null;
    }
  };

  const generateResponse = async (userMessage, chatId = null) => {
    try {
      const targetChatId = chatId || currentChatId;
      if (targetChatId && SSO) {

        const assistant = {
          modal_name: "openai/gpt-4.1",
          assistant_id: agencyInfo?.agentBuilderAssistantId,
          assistant_name: "agent builder",
        };

        await addNewMessage(
          targetChatId,
          userMessage,
          userMessage,
          "savetodb",
          selectedFiles,
          assistant,
          "notimage",
        );

        setSelectedFiles([]);
      } else {
        console.error("No chat ID or SSO available for sending message");
        showToast("Chat not ready. Please try again.");
      }
    } catch (error) {
      console.error("Error generating response:", error);
      showToast("Error sending message. Please try again.");
    }
  };


  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const userMessage = newMessage.trim();
    setNewMessage("");

    if (!currentChatId) {
      const newChatId = await createNewChat();
      if (newChatId) {
        setCurrentChatId(newChatId);
        await generateResponse(userMessage, newChatId);
      } else {
        showToast("Failed to create chat. Please try again.");
        setNewMessage(userMessage);
        return;
      }
    } else {
      await generateResponse(userMessage);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startListening = async () => {
    if (micStatus === "unknown") {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        micStreamRef.current = stream; // Save the stream reference
        setMicStatus("granted");
      } catch (err) {
        setMicStatus("denied");
        return;
      }
    }
    setRecordingLoading(true);
    SpeechRecognition.startListening({
      continuous: true,
      language: "en-US",
    });
  };

  const stopListening = () => {
    SpeechRecognition.stopListening();
    setRecordingLoading(false);
    resetTranscript();
    processedLen.current = 0;

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
      setMicStatus("unknown");
    }
  };

  const toggleMicrophone = () => {
    if (listening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    if (value === "") {
      processedLen.current = 0;
      resetTranscript();
    }
  };

  // Check browser support
  if (!browserSupportsSpeechRecognition) {
    console.warn("Browser doesn't support speech recognition.");
  }

  return (
    <>
      {agencyLoading ? (
        // Loading Screen
        <div className="flex-grow h-full flex items-center justify-center bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-black mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              Loading...
            </h2>
            <p className="text-gray-500">
              Please wait while we load your agent builder
            </p>
          </div>
        </div>
      ) : (
        // Main Messages Component
        <div className="flex-grow h-full flex flex-col bg-white border border-gray-200 rounded-lg shadow-lg">
          {/* Messages Area */}
          <div className="w-full flex-grow bg-gray-50 p-4 overflow-y-auto">
            {messages.length === 0
              ? ""
              : messages
                .filter(
                  (message) =>
                    message.content?.toLowerCase() !== "force restart" &&
                    !message.content?.toLowerCase().includes("edit mode"),
                )
                .map((message, index) => (
                  <div
                    key={message._id || index}
                    className={`flex ${message.sender === null ? "justify-end" : "items-end"
                      } mb-4`}
                  >
                    <div className="max-w-[80%] lg:max-w-[80%]">
                      <div
                        className={`px-4 py-3 rounded-2xl shadow-sm w-fit ml-auto ${message.sender === null
                          ? "bg-black text-white rounded-br-md"
                          : "bg-white border border-gray-200 rounded-bl-md"
                          }`}
                      >
                        <div
                          className={
                            message.sender === null
                              ? "text-white"
                              : "text-gray-800"
                          }
                        >
                          <div className="whitespace-pre-wrap break-words">
                            {message.sender === null ? (
                              message.content
                            ) : (
                              <MarkdownContent content={message.content} />
                            )}
                          </div>
                          {/* Show attached files if present */}
                        </div>
                        <div
                          className={`text-xs mt-1 ${message.sender === null
                            ? "text-gray-300"
                            : "text-gray-400"
                            }`}
                        >
                          {formatTime(message.time)}
                        </div>
                      </div>
                      {Array.isArray(message.file_ids) &&
                        message.file_ids.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2 ">
                            {message.file_ids.map((file, fileIdx) => (
                              <div
                                key={fileIdx}
                                className="bg-[#FFFFFF] shadow-sm rounded-full px-3 py-1 text-xs text-gray-700 flex items-center max-w-[200px] truncate"
                                title={file.name}
                              >
                                <Icon id="filePdf" className="mr-1" />
                                {file.name?.split("/")?.slice(-1)}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                ))}

            {/* Streaming Response */}
            {streamResponse && isStreaming && (
              <div className="flex items-end mb-4">
                <div className="max-w-xs lg:max-w-md">
                  <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                    <div className="text-xs text-gray-500 mb-1 font-bold">
                      AI Assistant
                    </div>
                    <div className="text-gray-800">
                      <MarkdownContent content={streamResponse} />
                    </div>
                    <div className="text-xs text-gray-400 mt-1">typing...</div>
                  </div>
                </div>
              </div>
            )}

            {/* Typing Indicator - Show only when context indicates typing and there's no streaming response */}
            {userTyping && currentChatId && (
              <div className="flex items-end mb-4">
                <div className="max-w-xs lg:max-w-md">
                  <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      AI is thinking...
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-gray-200 rounded-b-lg">
            {selectedFiles.length > 0 && (
              <div className="mt-2 flex flex-col items-start">
                <ul className="text-xs text-gray-700 flex flex-wrap gap-2">
                  {selectedFiles.map((file, idx) => (
                    <li
                      key={idx}
                      className="p-2 relative pr-4 bg-slate-100 rounded-full truncate max-w-[200px] mb-2 flex items-center"
                    >
                      {file.name?.split("/")?.slice(-1)}
                      <button
                        type="button"
                        className="absolute right-0 p-1 top-0 bottom-0 !bg-slate-100 text-black hover:text-red-500"
                        onClick={() =>
                          setSelectedFiles(
                            selectedFiles.filter((_, i) => i !== idx),
                          )
                        }
                        title="Remove file"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{ fontWeight: "bold", fontSize: "16px" }}>
                          &times;
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center space-x-3">
              <div className="flex  items-center flex-grow relative">
                <Uploadfile
                  classname="filecogChatBtn"
                  setSelectedFiles={setSelectedFiles}
                  chatId={threadId}
                  onFileSelect={onFileSelect}
                  setFileType={setFileType}
                />

                <input
                  value={newMessage}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  className="flex-grow px-4 py-3 pl-14 !outline-none text-gray-700 bg-gray-50 border border-gray-200 rounded-l-full focus:outline-none focus:ring-1 focus:ring-[#E5E7EB] focus:border-transparent"
                  type="text"
                  placeholder={
                    listening ? "Listening..." : "Type your message..."
                  }
                  disabled={isTyping}
                />

                {/* Voice Recognition Button */}
                <button
                  onClick={() => {
                    toggleMicrophone();
                  }}
                  style={{
                    margin: "0 20px",
                    cursor: "pointer",
                    borderRadius: "50%",
                    padding: "8px",
                  }}
                  title={listening ? "Stop recording" : "Start voice recording"}
                >
                  <Icon id="microphone" />
                </button>

                {/* Loading/Stop Button - Show only when recording */}
                {userTyping || recordingLoading ? (
                  <button
                    onClick={() => {
                      stopListening();
                    }}
                    title="Stop recording"
                  >
                    <Icon id="chatContinuous" />
                  </button>
                ) : (
                  <button
                    onClick={handleSendMessage}
                    disabled={isTyping || !newMessage.trim()}
                    className="sendChatBtn"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Icon id="sendChat" className="chat__input-icon" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Messages;
